/**
 * Hand-rolled SVG donut for the fleet's state distribution. No chart-
 * library dependency — the geometry is straight `<path d="A ...">`
 * arcs, one segment per state with a count > 0. Colors come from the
 * shared STATE_HEX map so the donut, the map pins, the StateBadge,
 * and the email template all stay synchronized.
 *
 * The center hole carries the total-boxes label so the donut works as
 * a stand-alone KPI on the Dashboard. Legend lives in a sibling
 * component so layouts can flow them (donut+legend side-by-side on
 * desktop, stacked on mobile) without forcing one shape.
 */
import React from "react";

import { STACKED_HEX, STATE_HEX } from "../../lib/colors";
import type { State } from "../../types/boxes";

/** Dashboard buckets the donut/legend render. Stacked boxes are an
 *  overlay (an UP-and-stacked box would otherwise be counted as UP);
 *  the Dashboard pulls them OUT of their underlying state's count and
 *  into this synthetic bucket so the donut shows them as their own
 *  slice. */
export type DashboardBucket = State | "STACKED";

interface StateDonutProps {
  counts: Partial<Record<DashboardBucket, number>>;
  size?: number;
  /** Stroke width as a fraction of radius (0..1). 0.45 ≈ classic donut. */
  thickness?: number;
}

const FULL_STATE_ORDER: DashboardBucket[] = [
  "UP",
  "SCANNER_ONLY",
  "CAMERA_ONLY",
  "UNKNOWN",
  "DOWN",
  "POWERED_DOWN",
  "UNTOUCHED",
  "STACKED",
];

function bucketColor(b: DashboardBucket): string {
  return b === "STACKED" ? STACKED_HEX : STATE_HEX[b];
}

function polar(cx: number, cy: number, r: number, angle: number): [number, number] {
  return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
}

function arcPath(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  start: number,
  end: number,
): string {
  // Donut "wedge": outer arc forward, line in, inner arc back, close.
  const [x1, y1] = polar(cx, cy, rOuter, start);
  const [x2, y2] = polar(cx, cy, rOuter, end);
  const [x3, y3] = polar(cx, cy, rInner, end);
  const [x4, y4] = polar(cx, cy, rInner, start);
  const largeArc = end - start > Math.PI ? 1 : 0;
  return [
    `M ${x1} ${y1}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4} ${y4}`,
    "Z",
  ].join(" ");
}

/** Center label shared by the multi-segment, single-segment, and empty
 *  donut renders. Positioned relative to the parent <svg>'s viewport.
 *  Empty state shows "—" instead of "0 boxes" so it reads as "no data"
 *  rather than "your fleet is empty". */
const CenterLabel: React.FC<{
  cx: number;
  cy: number;
  size: number;
  total: number;
}> = ({ cx, cy, size, total }) => {
  if (total === 0) {
    return (
      <text
        x={cx}
        y={cy + 6}
        textAnchor="middle"
        className="fill-secondary-400 text-base"
      >
        —
      </text>
    );
  }
  return (
    <>
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        className="fill-secondary-900 font-semibold"
        style={{ fontSize: size * 0.18 }}
      >
        {total}
      </text>
      <text
        x={cx}
        y={cy + size * 0.12}
        textAnchor="middle"
        className="fill-secondary-500"
        style={{ fontSize: size * 0.06 }}
      >
        boxes
      </text>
    </>
  );
};

export const StateDonut: React.FC<StateDonutProps> = ({
  counts,
  size = 240,
  thickness = 0.45,
}) => {
  const total = FULL_STATE_ORDER.reduce((s, st) => s + (counts[st] ?? 0), 0);
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 4; // leave 4px padding for stroke aesthetics
  const rInner = rOuter * (1 - thickness);
  const rMid = (rOuter + rInner) / 2;
  const strokeWidth = rOuter - rInner;
  const label = (
    <CenterLabel cx={cx} cy={cy} size={size} total={total} />
  );

  // Empty state — no boxes visible. Render a flat gray ring with "—".
  if (total === 0) {
    return (
      <svg width={size} height={size} role="img" aria-label="No boxes visible">
        <circle
          cx={cx}
          cy={cy}
          r={rMid}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        {label}
      </svg>
    );
  }

  // Build segments. Start at the top (-π/2) and go clockwise.
  let cursor = -Math.PI / 2;
  const segments = FULL_STATE_ORDER.flatMap((bucket) => {
    const v = counts[bucket] ?? 0;
    if (v === 0) return [];
    const sweep = (v / total) * Math.PI * 2;
    const start = cursor;
    const end = cursor + sweep;
    cursor = end;
    return [{ bucket, value: v, start, end }];
  });

  // Single-segment short-circuit: a full ring as a single circle reads
  // cleaner than a path that wraps 360° (which most SVG renderers
  // collapse to nothing because start == end).
  if (segments.length === 1) {
    const only = segments[0];
    return (
      <svg width={size} height={size} role="img" aria-label="Fleet health donut">
        <circle
          cx={cx}
          cy={cy}
          r={rMid}
          fill="none"
          stroke={bucketColor(only.bucket)}
          strokeWidth={strokeWidth}
        />
        {label}
      </svg>
    );
  }

  return (
    <svg width={size} height={size} role="img" aria-label="Fleet health donut">
      {segments.map((seg) => (
        <path
          key={seg.bucket}
          d={arcPath(cx, cy, rOuter, rInner, seg.start, seg.end)}
          fill={bucketColor(seg.bucket)}
        >
          <title>{`${seg.bucket}: ${seg.value}`}</title>
        </path>
      ))}
      {label}
    </svg>
  );
};

export default StateDonut;
